import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { trpc } from '@org/clients/school';
import {
  EventAgenda,
  EventDetailCard,
  badgeKindForEventKind,
  extractFirstUrl,
  stripUrlFromLocation,
  type AgendaItem,
  type EventAgendaLabels,
  type EventDetailCardLabels,
} from '@org/ui';
import { useTeacherId } from './teacher-picker';

type TeacherAgendaItem = AgendaItem & {
  eventKind: string | null;
  category: string | null;
  description: string | null;
  location: string | null;
  classId: string | null;
};

/**
 * Teacher dashboard agenda: every upcoming event the teacher hosts or is linked
 * to, each row showing the host + how many people are invited. Uses the same
 * shared EventAgenda/EventDetailCard as the student dashboard — rows open the
 * in-app read-only card, and the built-in camera join button appears inside
 * the join window.
 */
export function TeacherAgenda({ className }: { className?: string }) {
  const { t } = useTranslation('admin');
  // Detail-card labels are shared with the student calendar (p2e namespace).
  const { t: tp } = useTranslation('p2e');
  const navigate = useNavigate();
  const teacherId = useTeacherId();
  const [selectedItem, setSelectedItem] = useState<TeacherAgendaItem | null>(null);
  const { data, isLoading } = trpc.teacher.dashboard.agenda.useQuery({ teacherId });

  const items = useMemo<TeacherAgendaItem[]>(() => {
    const nowMs = Date.now();
    return (data ?? []).map((event) => {
      const startMs = new Date(event.startTime).getTime();
      const endMs = event.endTime ? new Date(event.endTime).getTime() : startMs + 60 * 60 * 1000;
      return {
        id: event.eventId,
        title: event.title,
        startTime: event.startTime,
        endTime: event.endTime,
        badgeKind: badgeKindForEventKind(event.eventKind),
        host: { name: event.hostName ?? null, avatarUrl: event.hostAvatarUrl ?? null },
        othersCount: event.participantsCount ?? 0,
        isLive: startMs <= nowMs && nowMs < endMs,
        joinHref: extractFirstUrl(event.location),
        href: null,
        eventKind: event.eventKind ?? null,
        category: event.category ?? null,
        description: event.description ?? null,
        location: event.location ?? null,
        classId: event.classId ?? null,
      };
    });
  }, [data]);

  const labels = useMemo<EventAgendaLabels>(
    () => ({
      title: t('teacher.dashboard.agenda.title'),
      empty: t('teacher.dashboard.agenda.empty'),
      today: t('p2e.dashboard.today'),
      tomorrow: t('p2e.dashboard.tomorrow'),
      startsInMinutes: (minutes) => t('p2e.dashboard.scheduled.startsInMinutes', { count: minutes }),
      startsAt: (time) => t('p2e.dashboard.scheduled.startsAt', { time }),
      kind: {
        group: t('p2e.dashboard.scheduled.kind.group'),
        personal: t('p2e.dashboard.scheduled.kind.personal'),
        event: t('p2e.dashboard.scheduled.kind.event'),
      },
      join: tp('calendar.detail.joinLink'),
    }),
    [t, tp],
  );

  const detailLabels = useMemo<EventDetailCardLabels>(
    () => ({
      date: tp('calendar.detail.date'),
      time: tp('calendar.detail.time'),
      teacher: tp('calendar.detail.teacher'),
      location: tp('calendar.detail.location'),
      join: tp('calendar.detail.joinLink'),
      goToClass: tp('calendar.detail.goToClass'),
      live: tp('calendar.detail.live'),
    }),
    [tp],
  );

  return (
    <>
      <EventAgenda
        items={items}
        isLoading={isLoading}
        labels={labels}
        className={className}
        onSelectItem={(item) => setSelectedItem(item as TeacherAgendaItem)}
      />

      <EventDetailCard
        event={
          selectedItem
            ? {
                title: selectedItem.title,
                kindLabel: selectedItem.eventKind
                  ? tp(`calendar.kinds.${selectedItem.eventKind}`, {
                      defaultValue: selectedItem.eventKind.replace('-', ' '),
                    })
                  : labels.kind[selectedItem.badgeKind],
                categoryLine: selectedItem.category,
                startTime: selectedItem.startTime,
                endTime: selectedItem.endTime,
                hostName: selectedItem.host.name,
                hostAvatarUrl: selectedItem.host.avatarUrl,
                locationText: stripUrlFromLocation(selectedItem.location),
                description: selectedItem.description,
                joinUrl: selectedItem.joinHref ?? null,
              }
            : null
        }
        labels={detailLabels}
        onClose={() => setSelectedItem(null)}
        onGoToClass={
          selectedItem?.classId
            ? () => {
                const { classId, id: eventId } = selectedItem;
                setSelectedItem(null);
                navigate({
                  to: '/classes/$classId/homework',
                  params: { classId: classId! },
                  search: { eventId, teacherId },
                });
              }
            : undefined
        }
      />
    </>
  );
}
